import {
  Card,
  CardContent,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  TextField,
  Button,
  Stack,
  Popover,
  Grid,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import Chart from "chart.js/auto";
import { ChartConfiguration, ChartItem } from "chart.js";
import { useQuery } from "@tanstack/react-query";
import { MoonLoader } from "react-spinners";
import {
  manageBookingsQueryOptions,
  manageListingsQueryOptions,
} from "../../queryOptions/ManageBookingsOptions";
import { manageMembershipsByAdmin } from "../../queryOptions/ManageMembership";
import { listAllEnrollments, listPrograms } from "../../apis/programsApi";
import { 
  BookOpen, 
  TrendingUp, 
  BarChart2, 
  Activity,
  RefreshCw,
  CalendarDays,
  Filter,
  Users,
  ChevronDown,
  GraduationCap,
} from "lucide-react";

// Updated interface definitions to match the new database structure
interface BookingInfo {
  id: string;
  bookingId: string;
  bookingDate: Date;
  bookingForDate: string;
  startTime: string;
  endTime: string;
  timeZone: string;
  status: string; // Current status from statusHistory
  payment: {
    id: string;
    chargeId: string;
    paymentStatus: string;
    subtotalAmount: number;
    taxAmount: number;
    totalAmount: number;
    totalHours: number;
    receiptUrl: string;
  };
  userDetails: {
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    address: string;
    userType: string;
    isGuest: boolean;
    isAuthenticated: boolean;
  };
  laneOrCage: {
    id: string;
    name: string;
    pricePerHour: number;
    type: string;
    size: string;
    image: string;
  };
  notificationData: any[];
  statusHistoryData: any[];
}

interface Booking {
  bookingInfo: BookingInfo;
}

interface BookingsResponse {
  bookings: Booking[];
}

interface ListingsResponse {
  laneOrCages: any[];
}

interface EarningsDataPoint {
  date: Date;
  amount: number;
  status: string;
  listingType?: string;
  userEmail?: string;
}

const CLINICS_BOOKING_EMAIL = "bookings@thebatcage.ca";

const formatDashboardCurrency = (amount: number) => {
  const formatted = Math.abs(amount).toFixed(2);
  return amount < 0 ? `-$${formatted}` : `$${formatted}`;
};

const Dashboard = () => {
  const {
    data: bookingsData,
    isLoading: bookingsLoading,
    isError: bookingsError,
  } = useQuery(manageBookingsQueryOptions());
  const {
    data: listingsData,
    isLoading: listingsLoading,
    isError: listingsError,
  } = useQuery(manageListingsQueryOptions());
  const {
    data: membershipsData,
    isLoading: membershipsLoading,
  } = useQuery(manageMembershipsByAdmin());
  const {
    data: enrollmentsData,
    isLoading: enrollmentsLoading,
  } = useQuery({
    queryKey: ["program-enrollments-all"],
    queryFn: listAllEnrollments,
  });
  const {
    data: programsData,
    isLoading: programsLoading,
  } = useQuery({
    queryKey: ["programs-all"],
    queryFn: listPrograms,
  });

  const bookingsResponse = bookingsData as BookingsResponse;
  const listingsResponse = listingsData as ListingsResponse;

  const [allEarningsData, setAllEarningsData] = useState<EarningsDataPoint[]>([]);

  // Separate filter states for each chart
  const [lineChartFilter, setLineChartFilter] = useState<string>("current_month");
  const [barChartFilter, setBarChartFilter] = useState<string>("current_month");
  const [bookingsChartFilter, setBookingsChartFilter] = useState<string>("current_month");

  // Calendar filter states
  const [dateFilterStart, setDateFilterStart] = useState<string>("");
  const [dateFilterEnd, setDateFilterEnd] = useState<string>("");
  const [isCalendarFilterActive, setIsCalendarFilterActive] = useState<boolean>(false);

  // Separate data states for each chart
  const [lineChartData, setLineChartData] = useState<EarningsDataPoint[]>([]);
  const [barChartData, setBarChartData] = useState<EarningsDataPoint[]>([]);
  const [bookingsChartData, setBookingsChartData] = useState<EarningsDataPoint[]>([]);
  const [bookingsSplitAnchor, setBookingsSplitAnchor] = useState<HTMLElement | null>(null);
  const [membershipSplitAnchor, setMembershipSplitAnchor] = useState<HTMLElement | null>(null);
  const [programsSplitAnchor, setProgramsSplitAnchor] = useState<HTMLElement | null>(null);

  // Create refs for the chart canvases
  const lineChartRef = useRef<HTMLCanvasElement | null>(null);
  const barChartRef = useRef<HTMLCanvasElement | null>(null);
  const bookingsChartRef = useRef<HTMLCanvasElement | null>(null);

  // Allowed booking statuses for metrics and charts
  const INCLUDED_BOOKING_STATUSES = [
    "confirmed",
    "pay_at_counter",
    "send_invoice",
    "invoice_sent",
    "rescheduled",
    "group_on",
  ];
  const TOTAL_BOOKING_STATUSES = [
    "confirmed",
    "pay_at_counter",
    "send_invoice",
    "invoice_sent",
    "group_on",
  ];
  const isIncludedStatus = (status?: string) =>
    !!status && INCLUDED_BOOKING_STATUSES.includes(status.toLowerCase());

  const getLocalDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const aggregateEarningsByDay = (data: EarningsDataPoint[]) => {
    const earningsByDay = data.reduce((acc, item) => {
      const key = getLocalDateKey(item.date);
      if (!acc[key]) {
        acc[key] = { date: item.date, amount: 0 };
      }
      acc[key].amount += item.amount;
      return acc;
    }, {} as Record<string, { date: Date; amount: number }>);

    return Object.values(earningsByDay).sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );
  };

  // References to chart instances for cleanup
  const lineChartInstance = useRef<Chart | null>(null);
  const barChartInstance = useRef<Chart | null>(null);
  const bookingsChartInstance = useRef<Chart | null>(null);

  // Filter change handlers
  const onLineChartFilterChange = (event: SelectChangeEvent) => {
    const selectedFilter = event.target.value as string;
    setLineChartFilter(selectedFilter);
    setIsCalendarFilterActive(false);
    const filteredData = filterDataByTimeRange(allEarningsData, selectedFilter);
    setLineChartData(filteredData);
    // Also update other charts to keep them in sync
    setBarChartData(filteredData);
    setBookingsChartData(filteredData);
  };

  const onBarChartFilterChange = (event: SelectChangeEvent) => {
    const selectedFilter = event.target.value as string;
    setBarChartFilter(selectedFilter);
    setIsCalendarFilterActive(false);
    const filteredData = filterDataByTimeRange(allEarningsData, selectedFilter);
    setBarChartData(filteredData);
    // Also update other charts to keep them in sync
    setLineChartData(filteredData);
    setBookingsChartData(filteredData);
  };

  const onBookingsChartFilterChange = (event: SelectChangeEvent) => {
    const selectedFilter = event.target.value as string;
    setBookingsChartFilter(selectedFilter);
    setIsCalendarFilterActive(false);
    const filteredData = filterDataByTimeRange(allEarningsData, selectedFilter);
    setBookingsChartData(filteredData);
    // Also update other charts to keep them in sync
    setLineChartData(filteredData);
    setBarChartData(filteredData);
  };

  // Calendar filter handlers
  const applyCalendarFilter = () => {
    if (!dateFilterStart || !dateFilterEnd) return;
    
    const startDate = new Date(dateFilterStart);
    const endDate = new Date(dateFilterEnd);
    
    const filteredData = allEarningsData.filter(
      (item) => item.date >= startDate && item.date <= endDate
    );
    
    // Update all chart data with the filtered data
    setLineChartData(filteredData);
    setBarChartData(filteredData);
    setBookingsChartData(filteredData);
    
    setIsCalendarFilterActive(true);
    
    // Reset dropdown filters
    setLineChartFilter("custom");
    setBarChartFilter("custom");
    setBookingsChartFilter("custom");
  };

  const clearCalendarFilter = () => {
    setDateFilterStart("");
    setDateFilterEnd("");
    setIsCalendarFilterActive(false);
    
    // Reset to default filter
    const defaultFilter = "current_month";
    setLineChartFilter(defaultFilter);
    setBarChartFilter(defaultFilter);
    setBookingsChartFilter(defaultFilter);
    
    // Apply default filter to all charts
    const defaultFilteredData = filterDataByTimeRange(allEarningsData, defaultFilter);
    setLineChartData(defaultFilteredData);
    setBarChartData(defaultFilteredData);
    setBookingsChartData(defaultFilteredData);
  };

  // Filter data based on selected time period
  const filterDataByTimeRange = (
    data: EarningsDataPoint[],
    filterValue: string
  ) => {
    if (filterValue === "custom") {
      return data; // Return as-is for custom filter
    }
    
    const currentDate = new Date();
    let filteredData = [...data];

    if (filterValue === "current_month") {
      const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      filteredData = data.filter(
        (item) => {
          return item.date >= firstDay && item.date <= lastDay;
        }
      );
    } else if (filterValue === "this_week") {
      const firstDayOfWeek = new Date(currentDate);
      firstDayOfWeek.setDate(currentDate.getDate() - currentDate.getDay()); // Sunday as first day
      filteredData = data.filter(
        (item) => item.date >= firstDayOfWeek && item.date <= currentDate
      );
    } else if (filterValue === "last_7_days") {
      const sevenDaysAgo = new Date(currentDate);
      sevenDaysAgo.setDate(currentDate.getDate() - 6);
      filteredData = data.filter(
        (item) => item.date >= sevenDaysAgo && item.date <= currentDate
      );
    } else if (filterValue === "last_30_days") {
      const thirtyDaysAgo = new Date(currentDate);
      thirtyDaysAgo.setDate(currentDate.getDate() - 29);
      filteredData = data.filter(
        (item) => item.date >= thirtyDaysAgo && item.date <= currentDate
      );
    } else if (filterValue === "last_month") {
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      filteredData = data.filter(
        (item) => item.date >= lastMonth && item.date <= currentDate
      );
    } else if (filterValue === "3_months") {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      filteredData = data.filter(
        (item) => item.date >= threeMonthsAgo && item.date <= currentDate
      );
    } else if (filterValue === "yearly") {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      filteredData = data.filter(
        (item) => item.date >= oneYearAgo && item.date <= currentDate
      );
    } else if (filterValue === "till_date") {
      // All time
      filteredData = data;
    }

    // Sort by date
    filteredData.sort((a, b) => a.date.getTime() - b.date.getTime());
    return filteredData;
  };

  const filterPointsByDashboardDate = (points: EarningsDataPoint[]) => {
    if (isCalendarFilterActive && dateFilterStart && dateFilterEnd) {
      const startDate = new Date(dateFilterStart);
      const endDate = new Date(dateFilterEnd);
      return points.filter(
        (item) => item.date >= startDate && item.date <= endDate
      );
    }
    return filterDataByTimeRange(points, lineChartFilter);
  };

  const sumFilteredEarnings = (points: EarningsDataPoint[]) =>
    filterPointsByDashboardDate(points)
      .reduce((total, item) => total + item.amount, 0)
      .toFixed(2);

  const isMembershipEarningIncluded = (membership: {
    status?: string;
  }) => membership.status?.toLowerCase() === "active";

  const PAID_PROGRAM_PAYMENT_STATUSES = [
    "success",
    "confirmed",
    "paid",
    "completed",
  ];

  const isProgramEarningIncluded = (enrollment: { paymentStatus?: string }) => {
    const status = enrollment.paymentStatus?.toLowerCase() ?? "";
    return PAID_PROGRAM_PAYMENT_STATUSES.includes(status);
  };

  // KPI cards use the same filtered dataset as the charts (date range / time filter)
  const totalBookings = lineChartData.filter((item) =>
    TOTAL_BOOKING_STATUSES.includes(item.status?.toLowerCase() ?? "")
  ).length;

  const getMembershipEarnings = () => {
    const memberships = Array.isArray(membershipsData) ? membershipsData : [];
    const points: EarningsDataPoint[] = memberships
      .filter((m: any) => isMembershipEarningIncluded(m))
      .map((m: any) => {
        const dateSource =
          m.lastPaymentDate ?? m.startDate ?? m.createdAt ?? m.updatedAt;
        return {
          date: new Date(dateSource),
          amount: Number(m.amount ?? 0),
          status: m.status ?? "",
        };
      })
      .filter((p) => !Number.isNaN(p.date.getTime()));

    return sumFilteredEarnings(points);
  };

  const getProgramEarnings = () => {
    const enrollments = Array.isArray(enrollmentsData) ? enrollmentsData : [];
    const points: EarningsDataPoint[] = enrollments
      .filter((e: any) => isProgramEarningIncluded(e))
      .map((e: any) => ({
        date: new Date(e.createdAt ?? e.selectedStartDate),
        amount: Number(e.totalAmount ?? 0),
        status: e.paymentStatus ?? "",
      }))
      .filter((p) => !Number.isNaN(p.date.getTime()) && p.amount > 0);

    return sumFilteredEarnings(points);
  };

  const getProgramsCount = (activeOnly = false) => {
    const programs = Array.isArray(programsData) ? programsData : [];
    return programs.filter((p: { isActive?: boolean }) =>
      activeOnly ? Boolean(p.isActive) : true
    ).length;
  };

  const getTotalEnrollmentsCount = () => {
    const enrollments = Array.isArray(enrollmentsData) ? enrollmentsData : [];
    const asPoints: EarningsDataPoint[] = enrollments
      .filter((e: { deletedAt?: string | null }) => !e.deletedAt)
      .map((e: { createdAt?: string | Date; selectedStartDate?: string; paymentStatus?: string }) => ({
        date: new Date(e.createdAt ?? e.selectedStartDate ?? ""),
        amount: 0,
        status: e.paymentStatus ?? "",
      }))
      .filter((p) => !Number.isNaN(p.date.getTime()));

    return filterPointsByDashboardDate(asPoints).length;
  };

  const getBookingEarningsByStatus = (status: string) => {
    return lineChartData
      .filter((item) => item.status?.toLowerCase() === status)
      .reduce((total, item) => total + item.amount, 0);
  };

  const isClinicsEmail = (email?: string) =>
    email?.toLowerCase() === CLINICS_BOOKING_EMAIL;

  const isPayAtCounterStatus = (status?: string) =>
    status?.toLowerCase() === "pay_at_counter";

  const getClinicsEarnings = () => {
    return lineChartData
      .filter(
        (item) => isPayAtCounterStatus(item.status) && isClinicsEmail(item.userEmail)
      )
      .reduce((total, item) => total + item.amount, 0);
  };

  const getAdjustedPayAtCounterEarnings = () => {
    return lineChartData
      .filter(
        (item) => isPayAtCounterStatus(item.status) && !isClinicsEmail(item.userEmail)
      )
      .reduce((total, item) => total + item.amount, 0);
  };

  const getGroupOnBookingsCount = () => {
    return lineChartData.filter(
      (item) => item.status?.toLowerCase() === "group_on"
    ).length;
  };

  const getConfirmedBookingsCount = () => {
    return lineChartData.filter(
      (item) => item.status?.toLowerCase() === "confirmed"
    ).length;
  };

  // Active memberships whose startDate falls in the selected date range / time filter
  const getActiveMembershipsCount = () => {
    const memberships = Array.isArray(membershipsData) ? membershipsData : [];
    const activeMemberships = memberships.filter(
      (m: { status?: string; startDate?: string | Date }) =>
        m.status?.toLowerCase() === "active" && !!m.startDate
    );

    const asPoints: EarningsDataPoint[] = activeMemberships.map(
      (m: { startDate: string | Date; status: string }) => ({
        date: new Date(m.startDate),
        amount: 0,
        status: m.status,
      })
    );

    return filterPointsByDashboardDate(asPoints).length;
  };

  const getPaymentDueMembershipsCount = () => {
    const memberships = Array.isArray(membershipsData) ? membershipsData : [];
    const dueMemberships = memberships.filter(
      (m: { status?: string }) => m.status?.toLowerCase() === "past_due"
    );
    const asPoints: EarningsDataPoint[] = dueMemberships
      .map(
        (m: {
          nextPaymentDate?: string | Date;
          startDate?: string | Date;
          createdAt?: string | Date;
          status: string;
        }) => ({
          date: new Date(m.nextPaymentDate ?? m.startDate ?? m.createdAt ?? ""),
          amount: 0,
          status: m.status,
        })
      )
      .filter((p) => !Number.isNaN(p.date.getTime()));

    return filterPointsByDashboardDate(asPoints).length;
  };

  // Process booking data when it's available
  useEffect(() => {
    if (bookingsResponse && bookingsResponse.bookings) {
      
      // Process the earnings data for charts using the new structure
      const processedData: EarningsDataPoint[] = bookingsResponse.bookings.map(
        (booking: any) => {
          const dataPoint = {
            date: new Date(booking.bookingInfo.bookingForDate),
            amount: booking.bookingInfo.payment?.totalAmount || 0, // Updated path with fallback
            status: booking.bookingInfo.status || "unknown", // Updated path with fallback
            listingType: booking.bookingInfo.laneOrCage?.type ?? "",
            userEmail: booking.bookingInfo.userDetails?.email ?? "",
          };
          return dataPoint;
        }
      );

      // Sort by date
      processedData.sort((a, b) => a.date.getTime() - b.date.getTime());

      setAllEarningsData(processedData);

      // Apply default filter to all charts initially
      const defaultFilteredData = filterDataByTimeRange(processedData, "current_month");
      setLineChartData(defaultFilteredData);
      setBarChartData(defaultFilteredData);
      setBookingsChartData(defaultFilteredData);
    }
  }, [bookingsData]);

  useEffect(() => {
    if (lineChartData.length > 0 && lineChartRef.current) {
      // Show all relevant booking statuses in the chart
      const relevantData = lineChartData.filter((item) => isIncludedStatus(item.status));
      const dailyEarnings = aggregateEarningsByDay(relevantData);

      const dates: string[] = dailyEarnings.map((item) =>
        item.date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })
      );

      const amounts: number[] = dailyEarnings.map((item) => item.amount);

      // Clean up previous chart instance
      if (lineChartInstance.current) {
        lineChartInstance.current.destroy();
      }

      // Initialize line chart
      const lineCtx = lineChartRef.current.getContext("2d");
      if (lineCtx) {
        const lineConfig: ChartConfiguration = {
          type: "line",
          data: {
            labels: dates,
            datasets: [
              {
                label: "Earnings",
                data: amounts,
                fill: true,
                backgroundColor: "rgba(59, 130, 246, 0.2)", // Tailwind blue-500 with opacity
                borderColor: "rgba(59, 130, 246, 1)", // Tailwind blue-500
                borderWidth: 2,
                tension: 0.4,
                pointRadius: 4,
                pointBackgroundColor: "rgba(59, 130, 246, 1)", // Tailwind blue-500
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: true,
                text: "Earnings Over Time",
                font: {
                  size: 16,
                },
              },
              legend: {
                display: true,
                position: "top",
              },
              tooltip: {
                callbacks: {
                  label: function (context) {
                    return `$${context.parsed.y}`;
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: "Amount ($)",
                },
                ticks: {
                  callback: function (value) {
                    return "$" + value;
                  },
                  maxTicksLimit: 10,
                },
              },
              x: {
                title: {
                  display: true,
                  text: "Booking Date",
                },
                ticks: {
                  maxTicksLimit: 10,
                },
              },
            },
          },
        };

        lineChartInstance.current = new Chart(lineCtx as ChartItem, lineConfig);
      }
    }

    return () => {
      if (lineChartInstance.current) {
        lineChartInstance.current.destroy();
      }
    };
  }, [lineChartData]);

  // Initialize and update the bar chart when its data changes
  useEffect(() => {
    if (barChartData.length > 0 && barChartRef.current) {
      // Show all relevant booking statuses in the chart
      const relevantData = barChartData.filter((item) => isIncludedStatus(item.status));
      const dailyEarnings = aggregateEarningsByDay(relevantData);

      const dates: string[] = dailyEarnings.map((item) =>
        item.date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })
      );
      const amounts: number[] = dailyEarnings.map((item) => item.amount);

      // Clean up previous chart instance
      if (barChartInstance.current) {
        barChartInstance.current.destroy();
      }

      // Initialize bar chart
      const barCtx = barChartRef.current.getContext("2d");
      if (barCtx) {
        const barConfig: ChartConfiguration = {
          type: "bar",
          data: {
            labels: dates,
            datasets: [
              {
                label: "Earnings",
                data: amounts,
                backgroundColor: "rgba(16, 185, 129, 0.6)",  // Tailwind green-500 with opacity
                borderColor: "rgba(16, 185, 129, 1)", // Tailwind green-500
                borderWidth: 1,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: true,
                text: "Earnings By Booking Date",
                font: {
                  size: 16,
                },
              },
              legend: {
                display: true,
                position: "top",
              },
              tooltip: {
                callbacks: {
                  label: function (context) {
                    return `$${context.parsed.y}`;
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: "Amount ($)",
                },
                ticks: {
                  callback: function (value) {
                    return "$" + value;
                  },
                  maxTicksLimit: 10,
                },
              },
              x: {
                title: {
                  display: true,
                  text: "Booking Date",
                },
                ticks: {
                  maxTicksLimit: 10,
                },
              },
            },
          },
        };

        barChartInstance.current = new Chart(barCtx as ChartItem, barConfig);
      }
    }

    return () => {
      if (barChartInstance.current) {
        barChartInstance.current.destroy();
      }
    };
  }, [barChartData]);

  // Initialize and update the bookings chart when its data changes
  useEffect(() => {
    if (bookingsChartData.length > 0 && bookingsChartRef.current) {
      // Show all relevant booking statuses in the chart
      const relevantData = bookingsChartData.filter((item) => isIncludedStatus(item.status));
      
      // Clean up previous chart instance
      if (bookingsChartInstance.current) {
        bookingsChartInstance.current.destroy();
      }

      const bookingsCtx = bookingsChartRef.current.getContext("2d");
      if (bookingsCtx) {
        // Group by date and count number of relevant bookings per date
        const bookingsByDate = relevantData.reduce((acc, item) => {
          const dateStr = item.date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
          acc[dateStr] = (acc[dateStr] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        // Convert to arrays for chart
        const bookingsDates = Object.keys(bookingsByDate);
        const bookingsCounts = Object.values(bookingsByDate);

        const bookingsConfig: ChartConfiguration = {
          type: "line",
          data: {
            labels: bookingsDates,
            datasets: [
              {
                label: "Bookings",
                data: bookingsCounts,
                fill: true,
                backgroundColor: "rgba(168, 85, 247, 0.2)", // Tailwind purple-500 with opacity
                borderColor: "rgba(168, 85, 247, 1)", // Tailwind purple-500
                borderWidth: 2,
                tension: 0.4,
                pointRadius: 4,
                pointBackgroundColor: "rgba(168, 85, 247, 1)", // Tailwind purple-500
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: true,
                text: "Bookings Over Time (By Booking Date)",
                font: {
                  size: 16,
                },
              },
              legend: {
                display: true,
                position: "top",
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: "Number of Bookings",
                },
                ticks: {
                  maxTicksLimit: 10,
                },
              },
              x: {
                title: {
                  display: true,
                  text: "Booking Date",
                },
                ticks: {
                  maxTicksLimit: 10,
                },
              },
            },
          },
        };

        bookingsChartInstance.current = new Chart(
          bookingsCtx as ChartItem,
          bookingsConfig
        );
      }
    }

    return () => {
      if (bookingsChartInstance.current) {
        bookingsChartInstance.current.destroy();
      }
    };
  }, [bookingsChartData]);

  // Loading state for the dashboard
  if (bookingsLoading || listingsLoading || membershipsLoading || enrollmentsLoading || programsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 400,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <MoonLoader color="#3b82f6" size={40} />
        <Typography sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <RefreshCw size={20} />
          Loading Dashboard Data...
        </Typography>
      </Box>
    );
  }

  // Error state handling
  if (bookingsError || listingsError) {
    return (
      <Card
        sx={{ 
          padding: 4, 
          textAlign: "center", 
          backgroundColor: "#fdeded",
          borderLeft: "4px solid #ef4444",
          borderRadius: "8px"
        }}
      >
        <Typography variant="h5" color="error" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Error loading dashboard data
        </Typography>
        <Typography sx={{ mt: 2 }}>
          Please try refreshing the page or check your connection.
        </Typography>
      </Card>
    );
  }

  const totalConfirmedBookings = getConfirmedBookingsCount();
  const totalGroupOnBookings = getGroupOnBookingsCount();
  const activeMembershipsCount = getActiveMembershipsCount();
  const paymentDueMembershipsCount = getPaymentDueMembershipsCount();
  const totalLanesOrCages = listingsResponse?.laneOrCages?.length || 0;
  const demoMembershipEarnings = getMembershipEarnings();
  const demoProgramEarnings = getProgramEarnings();
  const totalProgramsCount = getProgramsCount(false);
  const activeProgramsCount = getProgramsCount(true);
  const totalEnrollmentsCount = getTotalEnrollmentsCount();
  const confirmedEarnings = getBookingEarningsByStatus("confirmed");
  const clinicsEarnings = getClinicsEarnings();
  const payAtCounterEarnings = getAdjustedPayAtCounterEarnings();
  const isBookingsSplitOpen = Boolean(bookingsSplitAnchor);
  const isMembershipSplitOpen = Boolean(membershipSplitAnchor);
  const isProgramsSplitOpen = Boolean(programsSplitAnchor);

  return (
    <div className="p-4 bg-gray-50">
      {/* <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Business Dashboard
      </Typography> */}

      {/* Calendar Filter Section */}
      <Card sx={{ mb: 3, p: 2, backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarDays color="#3b82f6" size={20} />
          Date Range Filter
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            label="Start Date"
            type="date"
            value={dateFilterStart}
            onChange={(e) => setDateFilterStart(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
          />
          <TextField
            label="End Date"
            type="date"
            value={dateFilterEnd}
            onChange={(e) => setDateFilterEnd(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
          />
          <Button
            variant="contained"
            onClick={applyCalendarFilter}
            disabled={!dateFilterStart || !dateFilterEnd}
            sx={{ minWidth: 120 }}
            startIcon={<Filter size={16} />}
          >
            Apply Filter
          </Button>
          <Button
            variant="outlined"
            onClick={clearCalendarFilter}
            disabled={!isCalendarFilterActive}
            sx={{ minWidth: 120 }}
          >
            Clear Filter
          </Button>
        </Stack>
        {isCalendarFilterActive && (
          <Typography variant="body2" sx={{ mt: 1, color: "#10b981", fontWeight: 500 }}>
            Custom date range active: {dateFilterStart} to {dateFilterEnd}
          </Typography>
        )}
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            className="hover:shadow-lg transition-shadow duration-300"
            onClick={(event) => {
              setMembershipSplitAnchor(null);
              setProgramsSplitAnchor(null);
              setBookingsSplitAnchor(event.currentTarget);
            }}
            sx={{
              borderTop: "4px solid #3b82f6",
              height: "100%",
              cursor: "pointer",
              backgroundColor: "#eff6ff",
              outline: isBookingsSplitOpen ? "2px solid #3b82f6" : "none",
              outlineOffset: isBookingsSplitOpen ? "2px" : 0,
            }}
          >
            <CardContent>
              <div className="flex items-center justify-between">
                <Typography variant="subtitle1" color="text.secondary">
                  Bookings
                </Typography>
                <div className="bg-blue-100 p-2 rounded-full">
                  <BookOpen color="#3b82f6" size={24} />
                </div>
              </div>
              <Typography variant="h4" sx={{ mt: 2, fontWeight: "bold" }}>
                {totalBookings}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}
              >
                Click for breakdown
                <ChevronDown size={16} />
              </Typography>
            </CardContent>
          </Card>
          <Popover
            open={isBookingsSplitOpen}
            anchorEl={bookingsSplitAnchor}
            onClose={() => setBookingsSplitAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: { mt: 1, p: 2, minWidth: 280, borderRadius: 2 },
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Bookings breakdown
            </Typography>
            <Stack spacing={1.25}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#3b82f6" }} />
                  <Typography variant="body2">Total Bookings</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {totalBookings}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#2563eb" }} />
                  <Typography variant="body2">Confirmed Bookings</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {totalConfirmedBookings}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#8b5cf6" }} />
                  <Typography variant="body2">Group On Bookings</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {totalGroupOnBookings}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#10b981" }} />
                  <Typography variant="body2">Confirm Earnings</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatDashboardCurrency(confirmedEarnings)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#06b6d4" }} />
                  <Typography variant="body2">Clinics Bookings</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatDashboardCurrency(clinicsEarnings)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#f59e0b" }} />
                  <Typography variant="body2">Pay at Counter Earnings</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatDashboardCurrency(payAtCounterEarnings)}
                </Typography>
              </Box>
            </Stack>
          </Popover>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            className="hover:shadow-lg transition-shadow duration-300"
            onClick={(event) => {
              setBookingsSplitAnchor(null);
              setProgramsSplitAnchor(null);
              setMembershipSplitAnchor(event.currentTarget);
            }}
            sx={{
              borderTop: "4px solid #eab308",
              height: "100%",
              cursor: "pointer",
              backgroundColor: "#fefce8",
              outline: isMembershipSplitOpen ? "2px solid #eab308" : "none",
              outlineOffset: isMembershipSplitOpen ? "2px" : 0,
            }}
          >
            <CardContent>
              <div className="flex items-center justify-between">
                <Typography variant="subtitle1" color="text.secondary">
                  Membership
                </Typography>
                <div className="bg-yellow-100 p-2 rounded-full">
                  <Users color="#ca8a04" size={24} />
                </div>
              </div>
              <Typography variant="h4" sx={{ mt: 2, fontWeight: "bold" }}>
                {activeMembershipsCount}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}
              >
                Click for breakdown
                <ChevronDown size={16} />
              </Typography>
            </CardContent>
          </Card>
          <Popover
            open={isMembershipSplitOpen}
            anchorEl={membershipSplitAnchor}
            onClose={() => setMembershipSplitAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: { mt: 1, p: 2, minWidth: 280, borderRadius: 2 },
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Membership breakdown
            </Typography>
            <Stack spacing={1.25}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#8b5cf6" }} />
                  <Typography variant="body2">Active Members</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {activeMembershipsCount}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#ef4444" }} />
                  <Typography variant="body2">Payment Due</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {paymentDueMembershipsCount}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#f59e0b" }} />
                  <Typography variant="body2">Membership Earnings</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  ${demoMembershipEarnings}
                </Typography>
              </Box>
            </Stack>
          </Popover>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            className="hover:shadow-lg transition-shadow duration-300"
            onClick={(event) => {
              setBookingsSplitAnchor(null);
              setMembershipSplitAnchor(null);
              setProgramsSplitAnchor(event.currentTarget);
            }}
            sx={{
              borderTop: "4px solid #ec4899",
              height: "100%",
              cursor: "pointer",
              backgroundColor: "#fdf2f8",
              outline: isProgramsSplitOpen ? "2px solid #ec4899" : "none",
              outlineOffset: isProgramsSplitOpen ? "2px" : 0,
            }}
          >
            <CardContent>
              <div className="flex items-center justify-between">
                <Typography variant="subtitle1" color="text.secondary">
                  Programs
                </Typography>
                <div className="bg-pink-100 p-2 rounded-full">
                  <GraduationCap color="#ec4899" size={24} />
                </div>
              </div>
              <Typography variant="h4" sx={{ mt: 2, fontWeight: "bold" }}>
                {totalProgramsCount}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}
              >
                Click for breakdown
                <ChevronDown size={16} />
              </Typography>
            </CardContent>
          </Card>
          <Popover
            open={isProgramsSplitOpen}
            anchorEl={programsSplitAnchor}
            onClose={() => setProgramsSplitAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: { mt: 1, p: 2, minWidth: 280, borderRadius: 2 },
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Programs breakdown
            </Typography>
            <Stack spacing={1.25}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#ec4899" }} />
                  <Typography variant="body2">Total Programs</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {totalProgramsCount}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#10b981" }} />
                  <Typography variant="body2">Active Programs</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {activeProgramsCount}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#6366f1" }} />
                  <Typography variant="body2">Total Enrollment</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {totalEnrollmentsCount}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#f59e0b" }} />
                  <Typography variant="body2">Program Earnings</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  ${demoProgramEarnings}
                </Typography>
              </Box>
            </Stack>
          </Popover>
        </Grid>
      </Grid>

      {allEarningsData.length > 0 && (
        <>
          <Card sx={{ marginTop: 4, boxShadow: 2 }}>
            <CardContent>
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <TrendingUp color="#3b82f6" size={24} />
                  <Typography variant="h6">Earnings Over Time</Typography>
                </div>
                <FormControl variant="outlined" size="small">
                  <Select
                    value={lineChartFilter}
                    onChange={onLineChartFilterChange}
                    sx={{ 
                      width: "180px", 
                      height: "40px",
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6',
                      },
                    }}
                  >
                    <MenuItem value="current_month">Current Month</MenuItem>
                    <MenuItem value="this_week">This Week</MenuItem>
                    <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                    <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                    <MenuItem value="last_month">Last Month</MenuItem>
                    <MenuItem value="3_months">Past 3 Months</MenuItem>
                    <MenuItem value="yearly">Past Year</MenuItem>
                    <MenuItem value="till_date">All Time</MenuItem>
                    {isCalendarFilterActive && <MenuItem value="custom">Custom Range</MenuItem>}
                  </Select>
                </FormControl>
              </div>
              <div style={{ height: "400px", position: "relative" }}>
                {lineChartData.filter((item) => isIncludedStatus(item.status)).length > 0 ? (
                  <canvas ref={lineChartRef} />
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    color: 'text.secondary'
                  }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No Data Available
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                      {isCalendarFilterActive 
                        ? `No earnings data found for the selected date range (${dateFilterStart} to ${dateFilterEnd})`
                        : 'No earnings data found for the selected time period'
                      }
                    </Typography>
                  </Box>
                )}
              </div>
            </CardContent>
          </Card>

          <Card sx={{ marginTop: 4, boxShadow: 2 }}>
            <CardContent>
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <BarChart2 color="#10b981" size={24} />
                  <Typography variant="h6">Earnings By Booking Date</Typography>
                </div>
                <FormControl variant="outlined" size="small">
                  <Select
                    value={barChartFilter}
                    onChange={onBarChartFilterChange}
                    sx={{ 
                      width: "180px", 
                      height: "40px",
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#10b981',
                      },
                    }}
                  >
                    <MenuItem value="current_month">Current Month</MenuItem>
                    <MenuItem value="this_week">This Week</MenuItem>
                    <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                    <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                    <MenuItem value="last_month">Last Month</MenuItem>
                    <MenuItem value="3_months">Past 3 Months</MenuItem>
                    <MenuItem value="yearly">Past Year</MenuItem>
                    <MenuItem value="till_date">All Time</MenuItem>
                    {isCalendarFilterActive && <MenuItem value="custom">Custom Range</MenuItem>}
                  </Select>
                </FormControl>
              </div>
              <div style={{ height: "400px", position: "relative" }}>
                {barChartData.filter((item) => isIncludedStatus(item.status)).length > 0 ? (
                  <canvas ref={barChartRef} />
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    color: 'text.secondary'
                  }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No Data Available
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                      {isCalendarFilterActive 
                        ? `No earnings data found for the selected date range (${dateFilterStart} to ${dateFilterEnd})`
                        : 'No earnings data found for the selected time period'
                      }
                    </Typography>
                  </Box>
                )}
              </div>
            </CardContent>
          </Card>

          <Card sx={{ marginTop: 4, boxShadow: 2 }}>
            <CardContent>
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <Activity color="#a855f7" size={24} />
                  <Typography variant="h6">Bookings Over Time</Typography>
                </div>
                <FormControl variant="outlined" size="small">
                  <Select
                    value={bookingsChartFilter}
                    onChange={onBookingsChartFilterChange}
                    sx={{ 
                      width: "180px", 
                      height: "40px",
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#a855f7',
                      },
                    }}
                  >
                    <MenuItem value="current_month">Current Month</MenuItem>
                    <MenuItem value="this_week">This Week</MenuItem>
                    <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                    <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                    <MenuItem value="last_month">Last Month</MenuItem>
                    <MenuItem value="3_months">Past 3 Months</MenuItem>
                    <MenuItem value="yearly">Past Year</MenuItem>
                    <MenuItem value="till_date">All Time</MenuItem>
                    {isCalendarFilterActive && <MenuItem value="custom">Custom Range</MenuItem>}
                  </Select>
                </FormControl>
              </div>
              <div style={{ height: "400px", position: "relative" }}>
                {bookingsChartData.filter((item) => isIncludedStatus(item.status)).length > 0 ? (
                  <canvas ref={bookingsChartRef} />
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    color: 'text.secondary'
                  }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No Data Available
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                      {isCalendarFilterActive 
                        ? `No booking data found for the selected date range (${dateFilterStart} to ${dateFilterEnd})`
                        : 'No booking data found for the selected time period'
                      }
                    </Typography>
                  </Box>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Fallback display when no data */}
      {(!allEarningsData || allEarningsData.length === 0) && !bookingsLoading && (
        <Card sx={{ marginTop: 4, boxShadow: 2, backgroundColor: "#f8fafc" }}>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {bookingsResponse?.bookings?.length === 0 
                ? "No bookings found in the system." 
                : "Unable to process booking data. Please check the console for errors."
              }
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total Bookings: {totalBookings}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Listings: {totalLanesOrCages}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;